/**
 * Money in and money out, by what it was for.
 *
 * Nothing here knows which categories exist. Every row, and every option in
 * the category filter, comes from the server, which reads them from the ledger
 * and the category table -- so a category a Book Keeper created a minute ago
 * appears without anyone configuring this screen.
 *
 * Posted figures are the books. Lines categorised but not yet posted are not,
 * and are only counted when the reader asks for them, labelled as such.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { localIso, windowStart } from './CashPosition'
import { Card, Empty, ErrorNote, Loading, Note, Stat } from './ui'
import SearchSelect from './SearchSelect'

type Flow = 'in' | 'out'
type Status = 'posted' | 'unposted' | 'all'

interface GroupTotal {
  key: string
  name: string
  kind: string
  flow: Flow
  total_paise: number
  count: number
  posted_paise: number
  posted_count: number
  unposted_paise: number
  unposted_count: number
}

interface Option {
  key: string
  name: string
}

interface Report {
  since: string
  upto: string
  status: Status
  in_paise: number
  out_paise: number
  net_paise: number
  unposted_in_paise: number
  unposted_out_paise: number
  waiting_count: number
  waiting_in_paise: number
  waiting_out_paise: number
  money_in: GroupTotal[]
  money_out: GroupTotal[]
  categories: Option[]
  cost_centres: Option[]
  bank_accounts: Option[]
}

interface Line {
  date: string
  key: string
  category: string
  flow: Flow
  amount_paise: number
  posted: boolean
  status: string
  bank_account: string
  description: string
  counterparty: string | null
  reference: string | null
  journal_number: string | null
  cost_centre: string | null
  transaction_id: string | null
}

interface Lines {
  total_count: number
  in_paise: number
  out_paise: number
  lines: Line[]
}

interface Filters {
  since: string
  upto: string
  status: Status
  flow: '' | Flow
  category: string
  bank_account_id: string
  cost_centre: string
}

interface Drill {
  /** Null lists every line the filters keep. */
  key: string | null
  name: string
  flow: Flow | null
}

const STATUS_OPTIONS: { value: Status; label: string }[] = [
  { value: 'posted', label: 'Posted to the books' },
  { value: 'unposted', label: 'Not posted yet' },
  { value: 'all', label: 'Posted and not posted' },
]

const LINE_STATUS: Record<string, string> = {
  posted: 'In the books',
  imported: 'Imported',
  categorized: 'Categorised',
  reviewed: 'Reviewed',
  accounting_pending: 'Awaiting posting',
}

function toQuery(values: Record<string, string | number | null | undefined>): string {
  const query = new URLSearchParams()
  for (const [name, value] of Object.entries(values)) {
    if (value !== '' && value !== null && value !== undefined) query.set(name, String(value))
  }
  return query.toString()
}

function signed(paise: number): string {
  return paise < 0 ? `−${formatInr(-paise)}` : formatInr(paise)
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

export function CategoryMovement() {
  const today = new Date()
  const [filters, setFilters] = useState<Filters>({
    since: windowStart('fy', today),
    upto: localIso(today),
    status: 'posted',
    flow: '',
    category: '',
    bank_account_id: '',
    cost_centre: '',
  })
  const [drill, setDrill] = useState<Drill | null>(null)

  const set = <K extends keyof Filters>(name: K, value: Filters[K]) =>
    setFilters((current) => ({ ...current, [name]: value }))

  const backwards = !!filters.since && !!filters.upto && filters.upto < filters.since
  const ready = !!filters.since && !!filters.upto && !backwards

  const { data, isLoading, error } = useQuery({
    queryKey: ['category-movement', filters],
    queryFn: () => api.get<Report>(`/banking/category-movement?${toQuery({ ...filters })}`),
    enabled: ready,
    placeholderData: (previous) => previous,
  })

  const moneyIn = filters.flow !== 'out'
  const moneyOut = filters.flow !== 'in'

  const figureNote = (total: number, unposted: number) =>
    filters.status === 'posted'
      ? 'posted to the books'
      : filters.status === 'unposted'
        ? 'not posted yet — from the statement'
        : `${formatInr(total - unposted)} posted · ${formatInr(unposted)} not yet`

  return (
    <>
      <Card title="Money in and out by category" hint="from the ledger">
        <div className="filter-bar">
          <label>
            From
            <input type="date" value={filters.since} onChange={(e) => set('since', e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={filters.upto} onChange={(e) => set('upto', e.target.value)} />
          </label>
          <label>
            Category
            <SearchSelect
              aria-label="Category"
              value={filters.category}
              onChange={(v) => set('category', v)}
              empty="All categories"
              options={(data?.categories ?? []).map((o) => ({ value: o.key, label: o.name }))}
            />
          </label>
          <label>
            Money in / out
            <select value={filters.flow} onChange={(e) => set('flow', e.target.value as Filters['flow'])}>
              <option value="">Both</option>
              <option value="in">Money in</option>
              <option value="out">Money out</option>
            </select>
          </label>
          <label>
            Cost centre
            <select value={filters.cost_centre} onChange={(e) => set('cost_centre', e.target.value)}>
              <option value="">All cost centres</option>
              {(data?.cost_centres ?? []).map((o) => (
                <option key={o.key} value={o.key}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Bank account
            <select
              value={filters.bank_account_id}
              onChange={(e) => set('bank_account_id', e.target.value)}
            >
              <option value="">All accounts</option>
              {(data?.bank_accounts ?? []).map((o) => (
                <option key={o.key} value={o.key}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={filters.status} onChange={(e) => set('status', e.target.value as Status)}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      {backwards && <Note kind="error">The “To” date is before the “From” date.</Note>}
      {ready && isLoading && <Loading what="Adding up the categories" />}
      {error && <ErrorNote error={error} />}

      {data && ready && (
        <>
          <div className="grid">
            <Stat
              label="Total money in"
              value={formatInr(data.in_paise)}
              note={figureNote(data.in_paise, data.unposted_in_paise)}
            />
            <Stat
              label="Total money out"
              value={formatInr(data.out_paise)}
              note={figureNote(data.out_paise, data.unposted_out_paise)}
            />
            <Stat
              label="Net cash flow"
              value={signed(data.net_paise)}
              note={
                data.in_paise === 0 && data.out_paise === 0
                  ? 'nothing moved with these filters'
                  : data.net_paise > 0
                    ? 'more came in than went out'
                    : data.net_paise < 0
                      ? 'more went out than came in'
                      : 'in and out were equal'
              }
            />
          </div>

          {filters.status === 'posted' && data.waiting_count > 0 && (
            <Note kind="warn">
              {plural(data.waiting_count, 'line')} ({formatInr(data.waiting_in_paise)} in,{' '}
              {formatInr(data.waiting_out_paise)} out) {data.waiting_count === 1 ? 'is' : 'are'}{' '}
              imported or categorised but not posted, so {data.waiting_count === 1 ? 'it is' : 'they are'}{' '}
              not counted above.{' '}
              <button type="button" className="link-button" onClick={() => set('status', 'all')}>
                Include them
              </button>
            </Note>
          )}

          <div className="split">
            {moneyIn && (
              <GroupTable
                title="Money in by category"
                flow="in"
                groups={data.money_in}
                total={data.in_paise}
                status={filters.status}
                picked={drill}
                onPick={setDrill}
              />
            )}
            {moneyOut && (
              <GroupTable
                title="Money out by category"
                flow="out"
                groups={data.money_out}
                total={data.out_paise}
                status={filters.status}
                picked={drill}
                onPick={setDrill}
              />
            )}
          </div>

          {!drill && (data.money_in.length > 0 || data.money_out.length > 0) && (
            <p className="muted" style={{ margin: 0 }}>
              Click a category to see its transactions, or{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => setDrill({ key: null, name: 'Every transaction', flow: null })}
              >
                list every transaction
              </button>{' '}
              these filters keep.
            </p>
          )}

          {drill && <DrillDown filters={filters} drill={drill} onClose={() => setDrill(null)} />}
        </>
      )}
    </>
  )
}

function GroupTable({
  title,
  flow,
  groups,
  total,
  status,
  picked,
  onPick,
}: {
  title: string
  flow: Flow
  groups: GroupTotal[]
  total: number
  status: Status
  picked: Drill | null
  onPick: (drill: Drill) => void
}) {
  return (
    <Card title={title} hint={formatInr(total)} tight>
      {groups.length === 0 ? (
        <Empty>No money {flow === 'in' ? 'came in' : 'went out'} with these filters.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th className="num">Lines</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const share = total > 0 ? Math.max(0, Math.min(100, (g.total_paise / total) * 100)) : 0
              const isPicked = picked?.key === g.key && picked?.flow === flow
              return (
                <tr key={g.key} className={isPicked ? 'picked' : undefined}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => onPick({ key: g.key, name: g.name, flow })}
                    >
                      {g.name}
                    </button>
                    {g.kind !== 'category' && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {g.kind === 'uncategorised'
                          ? 'no category was given before posting'
                          : g.kind === 'transfer'
                            ? 'moved between your own accounts'
                            : 'posted by another document'}
                      </div>
                    )}
                    <div className={`share ${flow}`}>
                      <span style={{ width: `${share}%` }} />
                    </div>
                  </td>
                  <td className="num muted">{g.count}</td>
                  <td className="num">
                    {signed(g.total_paise)}
                    {status === 'all' && g.unposted_count > 0 && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {formatInr(g.unposted_paise)} not posted
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </Card>
  )
}

function DrillDown({
  filters,
  drill,
  onClose,
}: {
  filters: Filters
  drill: Drill
  onClose: () => void
}) {
  const params = {
    ...filters,
    category: drill.key ?? filters.category,
    flow: drill.flow ?? filters.flow,
    limit: 1000,
  }
  const { data, isLoading, error } = useQuery({
    queryKey: ['category-movement-lines', params],
    queryFn: () => api.get<Lines>(`/banking/category-movement/lines?${toQuery(params)}`),
  })

  const heading = drill.flow ? `${drill.name} · money ${drill.flow}` : drill.name

  return (
    <Card
      title={heading}
      hint={data ? `${plural(data.total_count, 'transaction')}, ${filters.since} to ${filters.upto}` : undefined}
      tight
      actions={
        <button type="button" className="small" onClick={onClose}>
          Close
        </button>
      }
    >
      {isLoading && <Loading what="Fetching the transactions" />}
      {error && <ErrorNote error={error} />}
      {data && data.lines.length === 0 && <Empty>No transactions match.</Empty>}
      {data && data.lines.length > 0 && (
        <>
          {data.lines.length < data.total_count && (
            <Note kind="warn">
              Showing the newest {data.lines.length} of {data.total_count}. The totals below
              count all of them; narrow the dates to list the rest.
            </Note>
          )}
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Party / description</th>
                <th>Reference</th>
                <th>In / out</th>
                <th className="num">Amount</th>
                <th>Category</th>
                <th>Cost centre</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((line, index) => (
                <tr key={`${line.transaction_id ?? line.journal_number}-${line.flow}-${index}`}>
                  <td className="date">{line.date}</td>
                  <td>
                    <div>{line.description}</div>
                    <div className="faint" style={{ fontSize: 12 }}>
                      {line.bank_account}
                    </div>
                  </td>
                  <td className="mono">
                    {line.reference ?? <span className="faint">—</span>}
                    {line.journal_number && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {line.journal_number}
                      </div>
                    )}
                  </td>
                  <td>{line.flow === 'in' ? 'Money in' : 'Money out'}</td>
                  <td className={`num ${line.flow}-amount`}>
                    {line.flow === 'in' ? '+' : '−'}
                    {signed(line.amount_paise)}
                  </td>
                  <td>{line.category}</td>
                  <td>{line.cost_centre ?? <span className="faint">—</span>}</td>
                  <td>
                    <span className={`pill ${line.posted ? 'pass' : 'advisory'}`}>
                      {LINE_STATUS[line.status] ?? line.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>All {plural(data.total_count, 'transaction')}</td>
                <td className="num" colSpan={4} style={{ textAlign: 'left' }}>
                  {data.in_paise !== 0 && <span className="in-amount">+{signed(data.in_paise)} in </span>}
                  {data.out_paise !== 0 && <span className="out-amount">−{signed(data.out_paise)} out</span>}
                </td>
              </tr>
            </tfoot>
          </table>
        </>
      )}
    </Card>
  )
}
